import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { motion } from "framer-motion";
import { trackEvent } from "@/lib/analytics";
import { rememberPostLoginRedirect } from "@/lib/post-login-redirect";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { ParallaxHero } from "@/components/ParallaxHero";
import { MygrowLogo } from "@/components/brand/MygrowLogo";

/**
 * Onde o login federado começa. É uma navegação de página inteira, **nunca**
 * `fetch`/axios: o 302 vai para o `login.microsoftonline.com`, XHR não pode
 * segui-lo (CORS) e o Entra precisa do contexto real do browser para a cookie de
 * sessão do diretório (S-062, Notes). Um `<a href>` entrega isso sozinho, sem
 * depender de JS.
 *
 * É a única porta. O formulário de e-mail e senha que ficava acima deste botão
 * saiu junto com `POST /api/auth/login` — a S-073 tinha decidido que os dois
 * caminhos conviveriam, e essa decisão foi revertida.
 */
const ENTRA_LOGIN_PATH = "/api/auth/entra/login";

/**
 * Traduz o `?sso_error=` que o callback devolve (S-073 decisão 3).
 *
 * O vocabulário espelha o `_ERROR_MAP` de `services/api/app/routers/auth_entra.py`.
 * É `switch` com as frases inline, e não um objeto indexado, porque o scanner do
 * `app-i18n-coverage.test.ts` é um regex sobre o texto e só enxerga a chamada de
 * tradução com a string literal ao lado — num mapa as frases sumiriam da varredura
 * e voltariam em inglês sem nada reprovar.
 *
 * `internal` e qualquer código desconhecido caem na frase genérica: a tela nunca
 * mostra o código cru nem fica em branco.
 */
function ssoErrorMessage(code: string, t: TFunction): string {
  switch (code) {
    case "not_configured":
      return t("Signing in with Microsoft is not set up in this environment.");
    case "unavailable":
      return t("Sign-in is unavailable right now. Try again in a moment.");
    case "invalid_request":
      return t("Your sign-in attempt expired. Please start over.");
    case "idp_unavailable":
      return t("Microsoft did not respond. Try again in a moment.");
    case "auth_failed":
      return t("We could not complete the sign-in with Microsoft.");
    case "not_provisioned":
      return t("Your account does not have access to myGROW. Talk to your workspace administrator.");
    default:
      return t("We could not complete the sign-in. Please try again.");
  }
}

export default function Login() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  // Lido uma vez, no mount: o efeito abaixo apaga o param da URL, e a mensagem
  // precisa sobreviver a isso.
  const [ssoErrorCode] = useState<string | null>(() => searchParams.get("sso_error"));

  // Tira o `sso_error` da URL depois de lido. Sem isto um F5 ressuscita um erro
  // que já passou, e o link fica compartilhável com a falha grudada.
  useEffect(() => {
    if (!searchParams.has("sso_error")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("sso_error");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const error = ssoErrorCode ? ssoErrorMessage(ssoErrorCode, t) : "";

  return (
    <ParallaxHero>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="relative z-10 w-full max-w-md px-4"
      >
        {/* O card cresceu quando o formulário saiu.
            Com `max-w-sm` e só um botão fantasma ele virava uma tira estreita no
            meio do hero, e a única ação da tela era o elemento menos visível
            dela. O botão passou a `action` — o mesmo amarelo que o "Sign In"
            tinha — porque agora ele não divide a atenção com nada: é a tela
            inteira. */}
        <Card className="border-white/10 bg-white/10 backdrop-blur-md shadow-2xl">
          <CardHeader className="text-center pb-3 pt-9">
            <div className="flex justify-center mb-4">
              <MygrowLogo className="h-12 w-auto text-white" />
            </div>
            <p className="text-base text-white/80">{t("Sign in to your account")}</p>
            <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-white/50">
              {t("Use your Exed Microsoft account. It is the only way in.")}
            </p>
          </CardHeader>
          <CardContent className="px-8 pb-8">
            {error && (
              <p role="alert" className="mb-4 text-sm text-red-400">
                {error}
              </p>
            )}

            <Button asChild variant="action" className="h-12 w-full text-[15px]">
              <a
                href={ENTRA_LOGIN_PATH}
                onClick={() => {
                  trackEvent("login_submitted", { login_method: "sso" });
                  // O destino não pode viajar na query da ida ao provedor (ADR
                  // #12), então fica guardado nesta aba e é consumido no retorno.
                  rememberPostLoginRedirect(searchParams.get("redirect"));
                }}
              >
                <MicrosoftLogo />
                {t("Sign in with Microsoft")}
              </a>
            </Button>

            <p className="mt-5 text-center text-[11px] leading-relaxed text-white/40">
              {t("You will be taken to Microsoft to confirm your identity.")}
            </p>

            <p className="mt-6 border-t border-white/10 pt-4 text-center text-[11px] text-white/50">
              {t("Protected by Exed Consulting")}
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </ParallaxHero>
  );
}

/**
 * Os quatro quadrados da marca. Inline porque o lucide não traz logos.
 *
 * Tamanho explícito: o `[&_svg]:size-4` do `Button` encolhia o logo para 16px, o
 * que ficava pequeno demais no botão de 48px que esta tela passou a usar.
 */
function MicrosoftLogo() {
  return (
    <svg viewBox="0 0 23 23" aria-hidden="true" focusable="false" className="!size-[18px]">
      <rect x="1" y="1" width="10" height="10" fill="#f25022" />
      <rect x="12" y="1" width="10" height="10" fill="#7fba00" />
      <rect x="1" y="12" width="10" height="10" fill="#00a4ef" />
      <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
    </svg>
  );
}
